import { Lock, CreditCard, Calendar } from "lucide-react";
import { useLocation } from "wouter";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { useAnalytics } from "@/hooks/useAnalytics";

const DEMO_BOOKING_URL = "https://viral.partners/umow-demo";

interface PaywallModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
}

export function PaywallModal({ open, onClose, title, description }: PaywallModalProps) {
  const { t } = useTranslation();
  const [, setLocation] = useLocation();
  const { trackEvent } = useAnalytics();

  const goToBilling = () => {
    trackEvent("upgrade_clicked", { click_location: "paywall_modal" });
    onClose();
    setLocation("/settings");
  };

  const bookDemo = () => {
    trackEvent("demo_prompt_clicked", { stage: "paywall" });
    window.open(DEMO_BOOKING_URL, "_blank", "noopener,noreferrer");
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#1a1c22] border border-white/10 text-white max-w-md">
        <DialogHeader className="items-center text-center">
          <div className="w-14 h-14 rounded-full bg-red-500/10 flex items-center justify-center mb-3 mx-auto">
            <Lock className="w-7 h-7 text-red-400" />
          </div>
          <DialogTitle className="text-xl font-semibold">{title || t("paywall.title")}</DialogTitle>
          <DialogDescription className="text-white/60 text-sm mt-2">
            {description || t("paywall.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 space-y-3">
          <Button
            onClick={goToBilling}
            className="w-full bg-gradient-to-r from-red-600 to-orange-500 hover:from-red-700 hover:to-orange-600 text-white"
          >
            <CreditCard className="w-4 h-4 mr-2" />
            {t("paywall.goToBilling")}
          </Button>
          <Button
            onClick={bookDemo}
            variant="outline"
            className="w-full border-orange-500/30 text-orange-400 hover:bg-orange-500/10 hover:text-orange-300"
          >
            <Calendar className="w-4 h-4 mr-2" />
            {t("paywall.bookDemo")}
          </Button>
          <Button
            variant="ghost"
            onClick={onClose}
            className="w-full text-white/50 hover:text-white hover:bg-white/5"
          >
            {t("paywall.close")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
